import React from "react";
import { getSignalSummary, SIGNAL_REGISTRY } from "../../../../config/signals";
import { BacktestRequest } from "../config";
import styles from "./StrategyConfigView.module.css";

interface StrategyConfigViewProps {
  config: BacktestRequest | null;
  className?: string;
  emptyMessage?: string;
}

const hasValue = (value: unknown): boolean =>
  value !== null && value !== undefined && value !== "";

const toTitleCase = (value: string): string =>
  value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatSignalLabel = (signalType: string): string => {
  return SIGNAL_REGISTRY[signalType]?.label ?? toTitleCase(signalType);
};

const formatSignalSummary = (signalType: string, rawParams: Record<string, unknown>): string => {
  const summary = getSignalSummary({
    id: `strategy-config-${signalType}`,
    type: signalType,
    params: rawParams,
  });

  if (summary) return summary;

  return Object.entries(rawParams)
    .filter(([, value]) => value !== null && value !== undefined && value !== "")
    .map(([key, value]) => {
      const paramLabel = SIGNAL_REGISTRY[signalType]?.params[key]?.label ?? toTitleCase(key);
      return `${paramLabel}: ${String(value)}`;
    })
    .join(", ");
};

const renderSignals = (
  groups: BacktestRequest["entry_signals"] | undefined,
  label: string,
): React.ReactNode => {
  if (!groups || groups.length === 0) return null;

  return (
    <div className={styles.configBlock}>
      <div className={styles.configBlockTitle}>{label}</div>
      {groups.map((andGroup, groupIdx) => (
        <div key={`signal-group-${label}-${groupIdx}`} className={styles.signalGroup}>
          {andGroup.map((signalSpec, signalIdx) => {
            const signalType = Object.keys(signalSpec)[0];
            if (!signalType) return null;

            const rawParams = signalSpec[signalType];
            const params =
              rawParams && typeof rawParams === "object"
                ? (rawParams as Record<string, unknown>)
                : {};
            const signalLabel = formatSignalLabel(signalType);
            const summary = formatSignalSummary(signalType, params);
            const chipText = `${signalLabel}${summary ? `: ${summary}` : ""}`;

            return (
              <span key={`signal-${label}-${groupIdx}-${signalIdx}`} className={styles.signalChip}>
                {chipText}
              </span>
            );
          })}
          {groupIdx < groups.length - 1 && (
            <span className={styles.orDivider}>OR</span>
          )}
        </div>
      ))}
    </div>
  );
};

export const StrategyConfigView: React.FC<StrategyConfigViewProps> = ({
  config,
  className,
  emptyMessage = "No configuration available",
}) => {
  if (!config || typeof config !== "object") {
    return (
      <div className={styles.emptyState}>
        <span className={styles.emptyText}>{emptyMessage}</span>
      </div>
    );
  }

  const strategyInfo = (config.strategy_info && typeof config.strategy_info === "object"
    ? config.strategy_info
    : {}) as BacktestRequest["strategy_info"];
  const signalMap = (config.signal_map && typeof config.signal_map === "object"
    ? config.signal_map
    : {}) as BacktestRequest["signal_map"];
  const legKeys = Object.keys(strategyInfo).sort((a, b) => Number(a) - Number(b));

  return (
    <div className={`${styles.configSection} ${className ?? ""}`.trim()}>
      <div className={styles.configBlock}>
        <div className={styles.configBlockTitle}>Date Range</div>
        <div className={styles.configRow}>
          <span className={styles.configKey}>Start</span>
          <span className={styles.configValue}>{config.start_date || "—"}</span>
        </div>
        <div className={styles.configRow}>
          <span className={styles.configKey}>End</span>
          <span className={styles.configValue}>{config.end_date || "—"}</span>
        </div>
      </div>

      {config.symbols && config.symbols.length > 0 && (
        <div className={styles.configBlock}>
          <div className={styles.configBlockTitle}>Symbols</div>
          <div className={styles.signalChipsWrap}>
            {config.symbols.map((symbol) => (
              <span key={symbol} className={styles.signalChip}>{symbol}</span>
            ))}
          </div>
        </div>
      )}

      {renderSignals(config.entry_signals, "Entry Signals")}
      {renderSignals(config.exit_signals, "Exit Signals")}

      {legKeys.map((key, idx) => {
        const tradeCondition = strategyInfo[key];
        const legSignals = signalMap?.[key];
        if (!tradeCondition || typeof tradeCondition !== "object") return null;

        let type: "Option" | "Stock" | "Future";
        let details: Record<string, unknown>;
        if ("Option" in tradeCondition) {
          type = "Option";
          details = tradeCondition.Option as unknown as Record<string, unknown>;
        } else if ("Stock" in tradeCondition) {
          type = "Stock";
          details = tradeCondition.Stock as unknown as Record<string, unknown>;
        } else {
          type = "Future";
          details = tradeCondition.Future as unknown as Record<string, unknown>;
        }

        return (
          <div key={`leg-${key}`} className={styles.configBlock}>
            <div className={styles.configBlockTitle}>Leg {idx + 1} - {type}</div>
            {hasValue(details.symbol) ? (
              <div className={styles.configRow}>
                <span className={styles.configKey}>Symbol</span>
                <span className={styles.configValue}>{String(details.symbol)}</span>
              </div>
            ) : null}
            {hasValue(details.direction) ? (
              <div className={styles.configRow}>
                <span className={styles.configKey}>Direction</span>
                <span className={styles.configValue}>{String(details.direction)}</span>
              </div>
            ) : null}
            {hasValue(details.quantity) ? (
              <div className={styles.configRow}>
                <span className={styles.configKey}>Quantity</span>
                <span className={styles.configValue}>{String(details.quantity)}</span>
              </div>
            ) : null}
            {hasValue(details.instrument) ? (
              <div className={styles.configRow}>
                <span className={styles.configKey}>Type</span>
                <span className={styles.configValue}>{String(details.instrument)}</span>
              </div>
            ) : null}
            {hasValue(details.strike) ? (
              <div className={styles.configRow}>
                <span className={styles.configKey}>Strike</span>
                <span className={styles.configValue}>{String(details.strike)}</span>
              </div>
            ) : null}
            {hasValue(details.dte) ? (
              <div className={styles.configRow}>
                <span className={styles.configKey}>DTE</span>
                <span className={styles.configValue}>{String(details.dte)}</span>
              </div>
            ) : null}
            {hasValue(details.delta) ? (
              <div className={styles.configRow}>
                <span className={styles.configKey}>Delta</span>
                <span className={styles.configValue}>{String(details.delta)}</span>
              </div>
            ) : null}

            {legSignals ? renderSignals(legSignals.entry_signals || [], `Leg ${idx + 1} Entry`) : null}
            {legSignals ? renderSignals(legSignals.exit_signals || [], `Leg ${idx + 1} Exit`) : null}
          </div>
        );
      })}
    </div>
  );
};
